import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import { UserAvatarDropdown } from '@/components/ui/UserAvatarDropdown';
import { Menu, X, FolderKanban, BarChart3, Settings } from 'lucide-react';
import { CreditBalanceWidget } from '@/components/layout/CreditBalanceWidget';

export function AppHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const isPublicRoute = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/register';

  const navigation = [
    { name: t('nav.boards'), href: '/boards', icon: FolderKanban },
    { name: t('nav.analytics'), href: '/analytics', icon: BarChart3 },
    { name: t('nav.settings'), href: '/settings', icon: Settings },
  ];

  if (isPublicRoute) {
    return (
      <header className="sticky top-0 z-[100] border-b bg-white/90 backdrop-blur dark:border-gray-800 dark:bg-gray-900/90">
        <div className="flex h-16 items-center justify-between px-6">
          <NavLink to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('app.name')}
            </span>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-6 md:flex">
            <a href="#features" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400">{t('nav.public.features')}</a>
            <a href="#pricing" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400">{t('nav.public.pricing')}</a>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <LanguageSwitcher />
              <NavLink to="/login">
                <Button variant="ghost" size="sm">{t('auth.login')}</Button>
              </NavLink>
              <NavLink to="/register">
                <Button variant="primary" size="sm">{t('auth.register')}</Button>
              </NavLink>
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="flex items-center gap-3 md:hidden">
            <LanguageSwitcher />
            <ThemeToggle />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-md p-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="flex flex-col space-y-4 py-4 px-6">
              <a href="#features" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400">{t('nav.public.features')}</a>
              <a href="#pricing" className="text-sm font-medium text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400">{t('nav.public.pricing')}</a>
              <div className="flex flex-col gap-2 pt-2">
                <NavLink to="/login">
                  <Button variant="ghost" size="sm" className="w-full justify-center">{t('auth.login')}</Button>
                </NavLink>
                <NavLink to="/register">
                  <Button variant="primary" size="sm" className="w-full justify-center">{t('auth.register')}</Button>
                </NavLink>
              </div>
            </div>
          </div>
        )}
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-[100] border-b bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex h-16 min-w-0 items-center justify-between gap-2 px-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-4 lg:gap-8">
          <NavLink to="/boards" className="flex min-w-0 items-center gap-2">
            <Logo className="h-8 sm:h-10" />
          </NavLink>

          <nav className="hidden md:flex items-center gap-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                    : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                >
                  <Icon className="size-4" />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>
        </div>

        <div className="flex min-w-0 shrink-0 items-center gap-1 sm:gap-3">
          <CreditBalanceWidget />
          <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1 hidden sm:block"></div>
          <div className="hidden min-[380px]:block">
            <LanguageSwitcher />
          </div>
          <div className="hidden sm:block">
            <ThemeToggle />
          </div>
          <UserAvatarDropdown />
        </div>
      </div>
    </header>
  );
}
